// client/components/Inventar/InventarBlockHistorie.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowRightLeft,
  FileUp,
  History,
  ImagePlus,
  PackageMinus,
  PackagePlus,
  Pencil,
  SlidersHorizontal,
} from "lucide-react";
import api from "../../constants/api";
import { showError } from "../../utils/toast.js";
import CollapsibleBlock from "../ui/CollapsibleBlock";
import BlockSectionToolbar from "../ui/BlockSectionToolbar.jsx";
import BlockSectionSearch from "../ui/BlockSectionSearch.jsx";
import AppButton from "../ui/AppButton.jsx";
import { Input } from "../forms/shared/FormAtoms.jsx";
import useIsMobile from "../ui/useIsMobile.js";
import ZuweisungBadge from "../ui/ZuweisungBadge.jsx";
import { segmentTabChipButtonClassName } from "../../design/tokens.js";
import { cn } from "../../utils/themeClasses.js";
import {
  enrichInventarHistoryRows,
  historyDayKey,
  historyDayLabel,
  historyRelTime,
  formatHistoryTime,
  inventarHistoryActionMeta,
  inventarHistoryActionKey,
  inventarHistoryLabelClass,
  inventarHistoryIconClass,
} from "../../utils/inventarHistory.js";

const ACTION_ICONS = {
  einbuchung: PackagePlus,
  abbuchung: PackageMinus,
  änderung: Pencil,
  umbuchung: ArrowRightLeft,
  foto_hochgeladen: ImagePlus,
  dokument_hochgeladen: FileUp,
  menge_update: SlidersHorizontal,
};

function HistoryActionIcon({ aktion }) {
  const key = inventarHistoryActionKey(aktion);
  const Icon = ACTION_ICONS[key] || History;
  return (
    <span className={inventarHistoryIconClass(aktion)} aria-hidden>
      <Icon className="h-3.5 w-3.5" strokeWidth={2} />
    </span>
  );
}

function HistoryDetailChips({ chips }) {
  if (!chips?.length) return null;
  return (
    <p className="mt-1 text-[11px] text-blue-200/55 leading-relaxed">
      {chips.map((c, i) => (
        <span key={c.key}>
          {i > 0 ? <span className="text-blue-200/30"> · </span> : null}
          <span className="text-blue-200/45">{c.label} </span>
          <span className="text-blue-100/75">{c.value}</span>
        </span>
      ))}
    </p>
  );
}

function HistoryTimelineEntry({ entry, isLast }) {
  const meta = inventarHistoryActionMeta(entry.aktion);
  const showChips = entry._chips?.length > 0 && (entry._chips.length > 1 || !entry._summary.includes("→"));

  return (
    <li className={cn("relative flex gap-2.5", isLast ? "pb-0" : "pb-3")}>
      {!isLast ? (
        <span
          className="absolute left-3.5 top-7 bottom-0 w-px -translate-x-1/2 bg-white/[0.06]"
          aria-hidden
        />
      ) : null}

      <HistoryActionIcon aktion={entry.aktion} />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 min-w-0">
            <span className={inventarHistoryLabelClass(entry.aktion)}>{meta.label}</span>
            <time className="text-[11px] tabular-nums text-blue-200/45" dateTime={entry.zeit}>
              {formatHistoryTime(entry.zeit)}
              {historyRelTime(entry.zeit) ? ` · ${historyRelTime(entry.zeit)}` : ""}
            </time>
          </div>
          {entry.user_name ? (
            <ZuweisungBadge
              user={{ id: entry.user_id, name: entry.user_name, avatar_url: entry.user_avatar }}
              variant="full"
              size="xs"
              truncateAt={22}
              title={entry.user_name}
              className="shrink-0"
            />
          ) : (
            <span className="text-[10px] text-blue-200/35 shrink-0">System</span>
          )}
        </div>

        {entry._summary ? (
          <p className="mt-0.5 text-sm text-blue-100/80 leading-snug">{entry._summary}</p>
        ) : null}

        {showChips ? <HistoryDetailChips chips={entry._chips} /> : null}
      </div>
    </li>
  );
}

export default function InventarBlockHistorie({ artikelId }) {
  const isMobile = useIsMobile();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [suche, setSuche] = useState("");
  const [debounced, setDebounced] = useState("");
  const [actionFilter, setActionFilter] = useState("__all__");
  const [limit, setLimit] = useState(50);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(suche.trim().toLowerCase()), 180);
    return () => clearTimeout(t);
  }, [suche]);

  const load = useCallback(async () => {
    if (!artikelId) return;
    try {
      setLoading(true);
      const r = await api.get(`/inventar/${artikelId}/history`);
      const rows = Array.isArray(r?.data) ? r.data : r?.data?.items || (Array.isArray(r?.data?.data) ? r.data.data : []);
      setItems(rows.filter(Boolean));
    } catch (e) {
      console.error(e);
      showError("Historie konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [artikelId]);

  useEffect(() => {
    load();
  }, [load]);

  const enriched = useMemo(() => enrichInventarHistoryRows(items), [items]);

  const actionOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    for (const e of enriched) {
      const key = inventarHistoryActionKey(e.aktion);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      opts.push({ value: key, label: inventarHistoryActionMeta(key).label });
    }
    return opts.sort((a, b) => a.label.localeCompare(b.label, "de"));
  }, [enriched]);

  const filtered = useMemo(() => {
    let rows = enriched;
    if (actionFilter !== "__all__") {
      rows = rows.filter((e) => inventarHistoryActionKey(e.aktion) === actionFilter);
    }
    if (debounced) {
      rows = rows.filter((e) => e._haystack.includes(debounced));
    }
    return rows;
  }, [enriched, debounced, actionFilter]);

  const grouped = useMemo(() => {
    const lim = filtered.slice(0, limit);
    const m = new Map();
    for (const r of lim) {
      const k = historyDayKey(r.zeit);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(r);
    }
    return m;
  }, [filtered, limit]);

  const hasActiveFilters = Boolean(debounced) || actionFilter !== "__all__";
  const sucheTrim = suche.trim();

  const headerInfo = useMemo(() => {
    if (!items.length) return "Keine Einträge";
    if (hasActiveFilters) return `${filtered.length} von ${items.length} Einträgen`;
    return `${items.length} Eintrag${items.length === 1 ? "" : "e"}`;
  }, [items.length, filtered.length, hasActiveFilters]);

  const resetFilters = () => {
    setSuche("");
    setActionFilter("__all__");
  };

  const canLoadMore = filtered.length > limit;

  return (
    <CollapsibleBlock
      compact
      title="Historie"
      headerInfo={headerInfo}
      headerInfoTitle={headerInfo}
      defaultOpen={false}
      panelId="inventar-historie-panel"
      contentClassName="px-3 sm:px-4 pb-3 sm:pb-4"
    >
      <BlockSectionToolbar
        className="mb-2"
        search={
          <BlockSectionSearch
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            onClear={() => setSuche("")}
            placeholder="Suche…"
            title="Aktion, Änderung oder Person"
            ariaLabel="Historie durchsuchen"
            wrapperClassName="relative w-full min-w-0 flex-1 xl:min-w-[20rem]"
          />
        }
        action={
          hasActiveFilters ? (
            <AppButton type="button" variant="secondary" size="sm" onClick={resetFilters} className="shrink-0">
              Filter zurücksetzen
            </AppButton>
          ) : null
        }
      />

      {actionOptions.length > 1 ? (
        <div className="mb-2 flex flex-wrap gap-1.5" role="group" aria-label="Nach Aktion filtern">
          <AppButton
            type="button"
            variant="ghost"
            onClick={() => setActionFilter("__all__")}
            className={cn(
              segmentTabChipButtonClassName,
              actionFilter === "__all__" ? "seg-btn-active-primary font-semibold" : "seg-btn-idle"
            )}
            aria-pressed={actionFilter === "__all__"}
          >
            Alle
          </AppButton>
          {actionOptions.map((opt) => (
            <AppButton
              key={opt.value}
              type="button"
              variant="ghost"
              onClick={() => setActionFilter(opt.value)}
              className={cn(
                segmentTabChipButtonClassName,
                actionFilter === opt.value ? "seg-btn-active-primary font-semibold" : "seg-btn-idle"
              )}
              aria-pressed={actionFilter === opt.value}
            >
              {opt.label}
            </AppButton>
          ))}
        </div>
      ) : null}

      {loading && !items.length ? (
        <div className="space-y-2.5 animate-pulse py-1" aria-busy="true">
          {[1, 2, 3].map((n) => (
            <div key={n} className="flex gap-2.5">
              <div className="h-7 w-7 rounded-full bg-white/[0.06]" />
              <div className="flex-1 space-y-1.5 pt-0.5">
                <div className="h-2.5 w-1/3 rounded bg-white/[0.06]" />
                <div className="h-3 w-2/3 rounded bg-white/[0.06]" />
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <p className="py-2 text-sm text-blue-200/60 italic">
          {items.length === 0 ? "Noch keine Änderungen protokolliert." : "Keine Treffer für Suche oder Filter."}
        </p>
      ) : (
        <div className="max-h-[min(48vh,24rem)] overflow-y-auto">
          <div className="space-y-3">
            {[...grouped.entries()].map(([day, rows]) => (
              <section key={day} aria-label={historyDayLabel(day)}>
                <h4 className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-blue-200/40">
                  {historyDayLabel(day)}
                </h4>
                <ol className="list-none m-0 p-0">
                  {rows.map((e, idx) => (
                    <HistoryTimelineEntry
                      key={e.id ?? `${e.aktion}-${e.zeit}`}
                      entry={e}
                      isLast={idx === rows.length - 1}
                    />
                  ))}
                </ol>
              </section>
            ))}
          </div>

          {canLoadMore ? (
            <AppButton
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setLimit((n) => n + 50)}
              className="w-full mt-3"
            >
              Weitere laden
            </AppButton>
          ) : null}
        </div>
      )}
    </CollapsibleBlock>
  );
}
